import type { dash_notification_plat } from "@dropbox/api-v2-client";
import type { APIv2Callable } from "@mirage/service-dbx-api/service";
import type { DashNotification } from "../data/notifications";
import { parseBluenotePayload } from "../data/notifications";

// These are coupled to the backend and need to be these specific string values
// because the bluenote payload has a per-app payload at these top level keys
export type BluenoteSurfaces = "dash-desktop" | "dash-web" | "dash-extension";

const surfaceToChannelId = (surface: BluenoteSurfaces) => {
  switch (surface) {
    case "dash-desktop":
      return { ".tag": "dnp_channel_desktop_push" as const };
    case "dash-web":
      return { ".tag": "dnp_channel_web_push" as const };
    case "dash-extension":
      return { ".tag": "dnp_channel_extension_push" as const };
    default:
      surface satisfies never;
      return { ".tag": "dnp_channel_extension_push" as const };
  }
};

export type DNPCategoryName = "feature_stack_shared";
const dnpCategoryToCategoryId = (category: DNPCategoryName) => {
  switch (category) {
    case "feature_stack_shared":
      return { ".tag": "dnp_category_feature_stack_shared" } as const;
    default:
      category satisfies never;
      return { ".tag": "dnp_category_unset" } as const;
  }
};

/**
 * Try to keep bluenote-specific logic in this layer
 * @param callApiV2 - The API v2 callable function
 * @param surface - The surface to retrieve notifications for
 * @param latestNid - Pass to refresh and load notifications for immediate display
 * @param oldestNid - Pass to load old notifications for paginating in the UI. Will be ignored if latestNid is also passed.
 * @returns Promise containing notifications and bolt data
 */
export const bluenoteRetrieve = async (
  callApiV2: APIv2Callable,
  surface: BluenoteSurfaces,
  latestNid?: string,
  oldestNid?: string,
): Promise<{
  notifications: DashNotification[];
  boltData: dash_notification_plat.BoltData;
}> => {
  let notificationRange;
  // Weird semantics but this is how you set up the backend to do an XOR for a parameter
  if (latestNid) {
    notificationRange = { ".tag": "start_nid" as const, start_nid: latestNid };
  } else if (oldestNid) {
    notificationRange = { ".tag": "end_nid" as const, end_nid: oldestNid };
  }
  const channelId = surfaceToChannelId(surface);
  const response = await callApiV2(
    "dashNotificationPlatDnpBluenotePushRetrieve",
    { channel_id: channelId, notification_range: notificationRange },
  );
  const notifications = (response.notifications ?? []).flatMap((notification) =>
    parseBluenotePayload(notification, surface),
  );
  return {
    notifications,
    boltData: response.bolt_data ?? {},
  };
};

export const actOnBluenote = async (
  callApiV2: APIv2Callable,
  args: dash_notification_plat.DNPBluenotePushActivityArg,
) => {
  // We don't have anything to do with the response, callers will catch exceptions
  await callApiV2("dashNotificationPlatDnpBluenotePushActivity", args);
};
export const dnpPreferenceGet = async (
  callApiV2: APIv2Callable,
): Promise<dash_notification_plat.DNPPreference[] | null> => {
  const channelId = surfaceToChannelId("dash-extension");
  const response = await callApiV2("dashNotificationPlatDnpPreferenceGet", {
    dnp_channel_id: channelId,
  });
  return response.dnp_preferences ?? null;
};

export const dnpPreferenceSet = async (
  callApiV2: APIv2Callable,
  dnpCategoryName: DNPCategoryName,
  isSubscribed: boolean,
): Promise<boolean> => {
  const channelId = surfaceToChannelId("dash-extension");
  const response = await callApiV2("dashNotificationPlatDnpPreferenceSet", {
    dnp_preference: {
      dnp_channel_id: channelId,
      dnp_category_id: dnpCategoryToCategoryId(dnpCategoryName),
      is_subscribed: isSubscribed,
    },
  });
  return response.success ?? false;
};
